import React, { useState, useRef, useEffect } from 'react';
import { EVENT_YEAR, PRICE_TENT, PRICE_ACCOMMODATION, CONTACT_EMAIL } from '../../utils/eventConfig';
import useFormScrolling from '../../hooks/useFormScrolling';
import { motion } from 'framer-motion';
import { useRegistration } from '../../context/RegistrationContext';
import { useModal } from '../../context/ModalContext';
import { Button } from '../../components/common/Button';
import Timer from '../../components/common/Timer';
import { submitRegistracia } from '../../api/letavyApi';

const RegistrationPage = () => {
  const { state, dispatch, actionTypes } = useRegistration();
  const { confirmAction } = useModal();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);

  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => {
      if (!errorRef.current) return;
      errorRef.current.focus({ preventScroll: true });
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => clearTimeout(id);
  }, [error]);
  
  // Aktivujeme hook pre automatické scrollovanie formulárov na mobilných zariadeniach
  useFormScrolling();
  
  const { formData, rezervacia, timeLeft, registrationSuccess } = state;
  
  const handleSubmit = async () => {
    if (!rezervacia || timeLeft <= 0) {
      setError('Vaša rezervácia vypršala. Prosím, začnite proces znova.');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Vytvorenie kópie formátových dát bez email_confirm
      const { email_confirm, ...registrationData } = formData;
      
      const response = await submitRegistracia(rezervacia.uuid, registrationData);
      
      if (response?.stav === 'ok') {
        dispatch({ type: actionTypes.SET_REGISTRATION_SUCCESS, payload: true });
      } else {
        setError(response.error || response.err || 'Registrácia zlyhala. Skúste to prosím znova.');
      }
    } catch (err) {
      console.error('Chyba pri odosielaní registrácie:', err);
      const errData = err.response?.data;
      const isMailError =
        errData?.exception?.toLowerCase().includes('transport') ||
        errData?.message?.toLowerCase().includes('smtp') ||
        errData?.message?.toLowerCase().includes('authenticate');

      if (isMailError) {
        setError(
          `Vaša registrácia bola zaznamenaná, ale potvrdzovací email sa nepodarilo odoslať. ` +
          `Skontrolujte doručenú poštu alebo kontaktujte organizátorov na ${CONTACT_EMAIL}.`
        );
      } else {
        setError('Nepodarilo sa odoslať registráciu. Skúste to prosím neskôr.');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleBack = () => {
    dispatch({ type: actionTypes.SET_STEP, payload: 4 });
  };
  
  const handleReset = () => {
    confirmAction(
      'Registrovať ďalšieho účastníka', 
      'Skutočne chcete začať novú registráciu pre ďalšieho účastníka?', 
      () => {
        dispatch({ type: actionTypes.RESET_STATE });
        dispatch({ type: actionTypes.SET_STEP, payload: 1 });
      },
      { confirmText: 'Nová registrácia', variant: 'default' }
    );
  };
  
  const handleRestart = () => {
    confirmAction(
      'Začať odznova', 
      'Skutočne chcete začať registráciu odznova? Všetky doteraz zadané údaje vrátane rezervácie budú stratené.', 
      () => {
        dispatch({ type: actionTypes.RESET_STATE });
        dispatch({ type: actionTypes.SET_STEP, payload: 1 });
      },
      { confirmText: 'Začať odznova', variant: 'warning' }
    );
  };

  return (
    <motion.div 
      className="container mx-auto px-4 py-8 max-w-3xl"
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -50 }}
      transition={{ duration: 0.3 }}
    >
      <div className="bg-white rounded-lg shadow-lg p-6 form-container">
        {registrationSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Registrácia úspešná!
            </h1>
            
            <p className="mb-6">
              Ďakujeme za registráciu na Letavy {EVENT_YEAR}. Na vašu emailovú adresu
              <span className="font-medium"> {formData.email}</span> bol odoslaný potvrdzujúci email s ďalšími informáciami.
            </p>
            
            <Button 
              variant="accent"
              onClick={handleReset}
            >
              Späť na úvodnú stránku
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Rekapitulácia údajov
            </h1>
            
            <p className="mb-6">
              Skontrolujte správnosť vašich údajov a potvrďte finálnu registráciu.
            </p>
            
            {rezervacia && timeLeft > 0 && (
              <Timer />
            )}
            
            {error && (
              <div
                ref={errorRef}
                tabIndex={-1}
                className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 outline-none"
              >
                <p className="text-red-700">{error}</p>
              </div>
            )}
            
            <div className="bg-background p-6 rounded-md mb-6">
              <h2 className="text-xl font-heading font-semibold text-primary mb-4">
                Zhrnutie registrácie
              </h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold">Osobné údaje</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Meno a priezvisko:</p>
                      <p>{formData.meno} {formData.priezvisko}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Dátum narodenia:</p>
                      <p>{formData.datum_narodenia}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Email:</p>
                      <p>{formData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Telefón:</p>
                      <p>{formData.telefon}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">ICE kontakt:</p>
                      <p>{formData.ice}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold">Adresa</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Ulica:</p>
                      <p>{formData.ulica}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Mesto:</p>
                      <p>{formData.mesto}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">PSČ:</p>
                      <p>{formData.psc}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Krajina:</p>
                      <p>{formData.krajina}</p>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold">Dielňa</h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">Názov dielne:</p>
                    <p>{formData.dielna}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold">Ubytovanie a strava</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Typ ubytovania:</p>
                      <p>{formData.ubytko_typ}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Typ stravy:</p>
                      <p>{formData.strava}</p>
                    </div>
                    {formData.ubytko_pozn && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-600">Poznámky k ubytovaniu:</p>
                        <p>{formData.ubytko_pozn}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold">Platba</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Spôsob platby:</p>
                      <p>{formData.platba}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Suma:</p>
                      <p className="font-semibold">
                        {formData.ubytko_typ === 'Stan(vlastný)' ? `${PRICE_TENT} EUR` : `${PRICE_ACCOMMODATION} EUR`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-between gap-4">
              <div className="flex gap-2">
                <Button 
                  variant="outline"
                  onClick={handleBack}
                  disabled={isLoading}
                >
                  Späť
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={handleRestart}
                  disabled={isLoading}
                  className="border-accent text-accent hover:bg-accent/10 hover:text-accent focus-visible:ring-accent"
                >
                  Začať odznova
                </Button>
              </div>
              
              <Button 
                variant="accent"
                onClick={handleSubmit}
                disabled={isLoading || timeLeft <= 0}
                className="relative"
              >
                {isLoading ? (
                  <>
                    <span className="opacity-0">Rekapitulácia údajov</span>
                    <span className="absolute inset-0 flex items-center justify-center">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  </>
                ) : (
                  'Dokončiť registráciu'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default RegistrationPage;
